import React, { useState } from 'react';
import { saveCaseDraft, quickAnalyze } from '../../services/factFinderApi';
import VoiceRecorder from './VoiceRecorder';
import analyticsTracker from '../../utils/analyticsTracker';

function QuickLogPage({ user, onBack, onOpenInvestigation, onExpandFullRecord }) {
  const now = new Date();
  const todayStr = now.toISOString().split('T')[0];
  const timeStr = now.toTimeString().slice(0, 5);

  React.useEffect(() => {
    analyticsTracker.trackFactFinderStarted('quick_log');
  }, []);

  const [caseData, setCaseData] = useState({
    mode: 'quick_log',
    incident_type: 'other',
    title: '',
    date: todayStr,
    time: timeStr,
    location: '',
    involved: '',
    narrative: '',
    witnesses: [],
    evidence: [],
  });

  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [error, setError] = useState('');

  function update(field, value) {
    setCaseData(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!caseData.narrative.trim()) {
      setError('Please describe what happened.');
      return;
    }
    setSaving(true);
    setError('');
    analyticsTracker.trackCaseCreated('fact_finder', 'quick_log');
    try {
      const result = await saveCaseDraft({ caseData, analysis: null });
      if (result.success) {
        setSavedId(result.case_id);
        analyticsTracker.trackFactFinderCompleted('quick_log');
        // Auto-run quick analysis after save
        handleQuickAnalyze();
      } else {
        setError(result.error || 'Failed to save.');
      }
    } catch (err) {
      setError(err.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleQuickAnalyze() {
    setAnalyzing(true);
    try {
      const result = await quickAnalyze(caseData.narrative);
      if (result.success) {
        setAiResult(result);
      }
    } catch {
      // Non-critical — silent fail for AI summary
    } finally {
      setAnalyzing(false);
    }
  }

  function handleExpandToInvestigation() {
    onOpenInvestigation({
      case_payload: {
        mode: 'investigation',
        incident_type: caseData.incident_type,
        title: caseData.title,
        date: caseData.date,
        time: caseData.time,
        location: caseData.location,
        supervisor: caseData.involved,
        narrative: caseData.narrative,
        department: '',
        crew: '',
        assignment: '',
        equipment: '',
        witnesses: [],
        evidence: [],
        follow_up_answers: {},
      }
    });
  }

  function handleExpandToFullRecord() {
    if (onExpandFullRecord) {
      onExpandFullRecord({
        case_payload: {
          mode: 'full_record',
          incident_type: caseData.incident_type,
          title: caseData.title,
          date: caseData.date,
          time: caseData.time,
          location: caseData.location,
          supervisor: caseData.involved,
          narrative: caseData.narrative,
          department: '',
          crew: '',
          assignment: '',
          equipment: '',
          witnesses: [],
          evidence: [],
          follow_up_answers: {},
        }
      });
    }
  }

  const isSaved = !!savedId;

  return (
    <div className="fact-finder-page quick-log-page">
      <div className="ff-container">
        {/* Header */}
        <div className="ff-im-header">
          <div className="ff-im-header-top">
            <button onClick={onBack} className="ff-back-btn" data-testid="back-btn">
              &larr; Back
            </button>
            <div className="ff-im-actions">
              <button
                onClick={handleSave}
                className="ff-btn-primary"
                disabled={saving || !caseData.narrative.trim()}
                data-testid="save-quick-log-btn"
              >
                {saving ? 'Saving...' : 'Save Quick Log'}
              </button>
            </div>
          </div>
          <div className="ff-im-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <img src="/stewie_front.png" alt="Stewie" data-testid="stewie-logo" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center 25%', border: '2px solid #334155' }} />
              <div className="ff-badge ff-badge-quick">Quick Log</div>
            </div>
            <h1>Capture the Facts Fast</h1>
            <p>Log what happened now and clean it up later.</p>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="ff-error-message" data-testid="error-message">{error}</div>
        )}

        {isSaved && (
          <div className="ff-success-message" data-testid="success-message">
            Quick log saved. Case ID: {savedId}
          </div>
        )}

        <div className="ff-ql-layout">
          {/* Main Form */}
          <div className="ff-ql-main">
            {!isSaved ? (
              <>
                {/* Guidance Card */}
                <div className="ff-card ff-guidance-card">
                  <h3>Quick Capture Tips</h3>
                  <ul>
                    <li>Stick to facts — what you saw, heard, did, were told</li>
                    <li>Capture details before you forget them</li>
                    <li>You can expand this later into a full record</li>
                  </ul>
                </div>

                {/* What Happened — Primary Field */}
                <div className="ff-card">
                  <h2>What happened?</h2>
                  <p className="ff-card-description">
                    Describe the incident in your own words. This is the only required field.
                  </p>
                  <textarea
                    className="ff-textarea"
                    value={caseData.narrative}
                    onChange={(e) => update('narrative', e.target.value)}
                    placeholder="Example: During the shift, I raised a safety concern about the lockout procedure. The supervisor told me to proceed anyway..."
                    rows={8}
                    data-testid="ql-narrative"
                    autoFocus
                  />
                  <div className="ff-narrative-footer">
                    <VoiceRecorder
                      onTranscript={(text) => {
                        setCaseData(prev => ({
                          ...prev,
                          narrative: prev.narrative ? prev.narrative + '\n\n' + text : text,
                        }));
                      }}
                    />
                  </div>
                </div>

                {/* Optional Details */}
                <div className="ff-card ff-optional-section">
                  <h2>Optional Details</h2>
                  <p className="ff-card-description">Add more context if you have time. All fields are optional.</p>
                  <div className="ff-form-grid">
                    <div className="ff-field">
                      <label className="ff-label">Title</label>
                      <input
                        className="ff-input"
                        value={caseData.title}
                        onChange={(e) => update('title', e.target.value)}
                        placeholder="Short title for this log"
                        data-testid="ql-title"
                      />
                    </div>
                    <div className="ff-field">
                      <label className="ff-label">Incident type</label>
                      <select
                        className="ff-select"
                        value={caseData.incident_type}
                        onChange={(e) => update('incident_type', e.target.value)}
                        data-testid="ql-type"
                      >
                        <option value="safety_concern">Safety Concern</option>
                        <option value="discipline_investigation">Discipline / Investigation</option>
                        <option value="supervisor_directive_conflict">Supervisor Directive</option>
                        <option value="contract_issue">Contract Issue</option>
                        <option value="injury_near_miss">Injury / Near Miss</option>
                        <option value="harassment_conduct">Harassment / Conduct</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="ff-field">
                      <label className="ff-label">Date</label>
                      <input
                        type="date"
                        className="ff-input"
                        value={caseData.date}
                        onChange={(e) => update('date', e.target.value)}
                        data-testid="ql-date"
                      />
                    </div>
                    <div className="ff-field">
                      <label className="ff-label">Time</label>
                      <input
                        type="time"
                        className="ff-input"
                        value={caseData.time}
                        onChange={(e) => update('time', e.target.value)}
                        data-testid="ql-time"
                      />
                    </div>
                    <div className="ff-field">
                      <label className="ff-label">Location</label>
                      <input
                        className="ff-input"
                        value={caseData.location}
                        onChange={(e) => update('location', e.target.value)}
                        placeholder="Station, yard, office, etc."
                        data-testid="ql-location"
                      />
                    </div>
                    <div className="ff-field">
                      <label className="ff-label">Who was involved?</label>
                      <input
                        className="ff-input"
                        value={caseData.involved}
                        onChange={(e) => update('involved', e.target.value)}
                        placeholder="Names or roles"
                        data-testid="ql-involved"
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Post-Save Output */
              <div className="ff-card ff-saved-output" data-testid="ql-saved-output">
                <div className="ff-saved-header">
                  <div className="ff-saved-check">&#10003;</div>
                  <div>
                    <h2>Quick Log Saved</h2>
                    <p className="ff-card-description">
                      Logged at {caseData.date} {caseData.time}
                    </p>
                  </div>
                </div>

                {analyzing && (
                  <div className="ff-analyzing-indicator">
                    Generating AI summary...
                  </div>
                )}

                {aiResult && (
                  <div className="ff-ai-summary">
                    <h3>AI Summary</h3>
                    <p>{aiResult.summary}</p>
                    <div className="ff-next-step">
                      <strong>Recommended next step:</strong> {aiResult.next_step}
                    </div>
                  </div>
                )}

                <div className="ff-saved-actions">
                  <button
                    className="ff-btn-primary"
                    onClick={handleExpandToFullRecord}
                    data-testid="expand-full-record-btn"
                  >
                    Expand into Full Record
                  </button>
                  <button
                    className="ff-btn-secondary"
                    onClick={handleExpandToInvestigation}
                    data-testid="expand-investigation-btn"
                  >
                    Open in Investigation Mode
                  </button>
                  <button
                    className="ff-btn-outline"
                    onClick={onBack}
                    data-testid="done-btn"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuickLogPage;
